if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      // The default add-to-cart button in the main product form is the source of truth.
      // Other pages name their own button through data-main-button.
      this.mainButton = this.dataset.mainButton
        ? document.querySelector(this.dataset.mainButton)
        : document.getElementById('add_to_cart');

      this.qtyInput = this.querySelector('[data-sticky-qty]');

      if (this.dataset.hideWhenVisible) {
        // Section mode (monthly selection page): nothing to buy, nothing to show.
        if (!this.mainButton) return;
        this.initSectionObserver();
      } else {
        this.observeTarget = this.mainButton || document.querySelector('.product-form__buttons');
        if (!this.observeTarget) return;
        this.initObserver();
      }
      this.initQuantity();
      this.initAddButton();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }

    // Reveal the banner once the default button has scrolled above the viewport,
    // hide it again when it scrolls back into view.
    initObserver() {
      if (!('IntersectionObserver' in window)) return;

      const headerOffset = this.getHeaderOffset();

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < headerOffset;
          this.classList.toggle('is-visible', scrolledPast);
          this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
        });
      }, { threshold: 0, rootMargin: `-${headerOffset}px 0px 0px 0px` });

      this.observer.observe(this.observeTarget);
    }

    // Section mode: show once the reveal-after element (the page's hero) has
    // scrolled up behind the header, and hide while the buy section it stands
    // in for is on screen, so the two never double up.
    //
    // Measured on scroll rather than with an IntersectionObserver: the hero is
    // an eager image whose box is still empty when an observer takes its first
    // reading, which revealed the banner while the cover was on screen.
    initSectionObserver() {
      this.revealAfter = this.dataset.revealAfter
        ? document.querySelector(this.dataset.revealAfter)
        : null;
      this.hideWhen = document.querySelector(this.dataset.hideWhenVisible);

      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updateSectionVisibility();
        });
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.updateSectionVisibility();
    }

    updateSectionVisibility() {
      const headerOffset = this.getHeaderOffset();
      const pastStart = this.revealAfter
        ? this.revealAfter.getBoundingClientRect().bottom < headerOffset
        : window.scrollY > 0;

      let buyInView = false;
      if (this.hideWhen) {
        const rect = this.hideWhen.getBoundingClientRect();
        buyInView = rect.top < window.innerHeight && rect.bottom > 0;
      }

      this.setVisible(pastStart && !buyInView);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    getHeaderOffset() {
      if (window.matchMedia('(max-width: 767px)').matches) return 0;
      const stickyHeader = document.querySelector('sticky-header');
      return stickyHeader ? Math.round(stickyHeader.getBoundingClientRect().height) : 0;
    }

    initQuantity() {
      const plus = this.querySelector('[data-sticky-qty-plus]');
      const minus = this.querySelector('[data-sticky-qty-minus]');
      if (plus) plus.addEventListener('click', () => this.stepQuantity(1));
      if (minus) minus.addEventListener('click', () => this.stepQuantity(-1));
    }

    stepQuantity(delta) {
      if (!this.qtyInput) return;
      const next = (parseInt(this.qtyInput.value, 10) || 1) + delta;
      this.qtyInput.value = next < 1 ? 1 : next;
    }

    // Forward the add to the main form so ajax cart + variant selection are reused.
    initAddButton() {
      const addButton = this.querySelector('[data-sticky-add]');
      if (!addButton || !this.mainButton) return;

      addButton.addEventListener('click', () => {
        const mainQty = this.dataset.mainQuantity
          ? document.querySelector(this.dataset.mainQuantity)
          : document.getElementById(`Quantity-${this.dataset.sectionId}`);
        if (mainQty && this.qtyInput) mainQty.value = this.qtyInput.value || 1;
        this.mainButton.click();
      });
    }
  });
}
